import { TWENTY_SECONDS } from '@/constants/time';
import type { ChatConversation } from '@/services/chat/chatTransport';

export type ReadOnlyInquiryConversation = ChatConversation & { stop: () => void };

/**
 * The conversations this module built. Membership is what makes a chat
 * read-only, so the chat never has to guess it from the inquiry's participant
 * ids, which would also catch a support account that Twilio does let send.
 */
const readOnlyConversations = new WeakSet<ChatConversation>();

type SharedConversation = {
  conversation: Promise<ReadOnlyInquiryConversation>;
  holders: number;
};

/**
 * One polling conversation per inquiry, however many chats show it. The
 * desktop inbox loads the selected inquiry in two places at once, and each
 * building its own would fetch and poll every message twice.
 */
const sharedConversations = new Map<string, SharedConversation>();

/**
 * Whether a chat is showing an inquiry read-only, with no composer, inquiry
 * actions or read receipt. True only for a conversation built by
 * {@link loadReadOnlyInquiryConversation}, so a Twilio or test-transport
 * conversation is never mistaken for one.
 */
export function isReadOnlyConversation(conversation: ChatConversation | undefined): boolean {
  return !!conversation && readOnlyConversations.has(conversation);
}

async function createReadOnlyInquiryConversation(
  conversationId: string
): Promise<ReadOnlyInquiryConversation> {
  const [{ PollingChatConversation }, { getInquiryMessages }] = await Promise.all([
    import('@/services/chat/pollingChatTransport'),
    import('@/services/getInquiryMessages'),
  ]);

  const messages = await getInquiryMessages(conversationId);

  const conversation = new PollingChatConversation(
    conversationId,
    getInquiryMessages,
    TWENTY_SECONDS,
    messages
  );

  readOnlyConversations.add(conversation);

  conversation.start();

  return conversation;
}

/**
 * An inquiry's conversation read from our API instead of Twilio, for staff
 * acting as Primum Support who open an inquiry they are not part of.
 *
 * Twilio has no way to read a conversation without joining it, and joining
 * would put Primum Support in the participant list with the right to send. The
 * API, though, lets the support account read any inquiry, so its messages come
 * from there, polled every twenty seconds for new ones, and the chat renders
 * them as it would Twilio's. Staff read to confirm, not to chat, so a slower
 * poll than the test transport's is enough.
 *
 * The first fetch both seeds the conversation and surfaces a refusal, so it
 * runs once rather than again inside a hydrate that would swallow its error.
 * A refusal is not shared, so the next open asks again.
 *
 * Every call must be matched by one {@link releaseReadOnlyInquiryConversation}
 * once its result is no longer shown, including a result that arrives after
 * the caller stopped wanting it. The last release stops the polling.
 *
 * The polling conversation and the message read are loaded with a dynamic
 * import, as the test transport is, so they stay out of the main bundle for
 * everyone who never opens an inquiry this way.
 */
export function loadReadOnlyInquiryConversation(
  conversationId: string
): Promise<ReadOnlyInquiryConversation> {
  const existing = sharedConversations.get(conversationId);

  if (existing) {
    existing.holders += 1;

    return existing.conversation;
  }

  const conversation = createReadOnlyInquiryConversation(conversationId);

  sharedConversations.set(conversationId, { conversation, holders: 1 });

  conversation.catch(() => {
    if (sharedConversations.get(conversationId)?.conversation === conversation) {
      sharedConversations.delete(conversationId);
    }
  });

  return conversation;
}

/** Lets go of a conversation from {@link loadReadOnlyInquiryConversation}. */
export function releaseReadOnlyInquiryConversation(
  conversation: ReadOnlyInquiryConversation
): void {
  const shared = sharedConversations.get(conversation.id);

  if (!shared) {
    conversation.stop();

    return;
  }

  shared.holders -= 1;

  if (shared.holders > 0) {
    return;
  }

  sharedConversations.delete(conversation.id);

  conversation.stop();
}
